import { message } from "antd";
import React, { useEffect, useState } from "react";

import { Tags } from "../../../types/tags";
import { NewMemberTag } from "../../lib/discordTypes";
import { getSupaClient, getWorkspace } from "../../lib/supabase";
import { TAG_COLORS } from "../../utils/constants";

/**
 * Purpose: fetch all tags for a guild and manage their deletion and edition
 * 
 * @param guildId - the guild id
 * @returns 
 *  data: tags data
 *  isLoading: wether the hook is still loading
 *  selectedTag: tag to delete or update
    isEditTagModalVisible: wether the edit tag modal is visible
    isDeleteTagModalVisible: wether the delete tag modal is visible
    setSelectedTag: state function to update the selected tag to edit/delete 
    setIsEditTagModalVisible: state function to update wether the the edit tag modal is visible
    setIsDeleteTagModalVisible: state function to update wether the delete tag modal is visible
    updateTag: function to update/edit the selected tag
    deleteTag: function to delete the selected tag
 */
function useGuildTags(guildId: string) {
  const [loading, setLoading] = useState<boolean>(true);
  const [guildTags, setGuildTags] = useState<Tags[]>([]);
  const [selectedTag, setSelectedTag] = useState<any>();
  const [isEditTagModalVisible, setIsEditTagModalVisible] =
    useState<boolean>(false);
  const [isDeleteTagModalVisible, setIsDeleteTagModalVisible] =
    useState<boolean>(false);
  const [isAddTagModalVisible, setIsAddTagModalVisible] =
    useState<boolean>(false);
  const supaClient = getSupaClient();
  const workspace = getWorkspace(supaClient);

  /**
   * Purpose: Load all tags for a given guild
   */
  const loadGuildTags = async () => {
    setLoading(true);
    const { data, error } = await supaClient
      .from<Tags>("tags")
      .select("id, name, color, description")
      .eq("guild", guildId);
    if (data) {
      setGuildTags(data);
    }
    if (error) {
      console.log("Error:", error);
    }
    setLoading(false);
  };

  /**
   *  Purpose: update/edit selected tag
   */
  const updateTag = async (): Promise<any> => {
    // console.log("update", selectedTag, selectedTag);
    setLoading(true);

    const { data, error } = await supaClient
      .from<Tags>("tags")
      .update(selectedTag)
      .match({ id: selectedTag && selectedTag?.id });

    if (error) {
      setLoading(false);
      // message.error("Error editing tag. Please try again");
    }
    if (data) {
      setIsEditTagModalVisible(false);
      await loadGuildTags();
      setLoading(false);
      // message.success("Edited successfully");
    }
  };

  /**
   * Purpose: add a new tag
   */
  const addTag = async (tag: NewMemberTag): Promise<any> => {
    setLoading(true);
    // code from MemberTags.jsx
    const getNewTagColor = async () => {
      let tagColor = "";
      const { count: teamTagsCount, error: teamTagsCountError } =
        await supaClient.from("tags").select("*", { count: "exact" });
      if (teamTagsCountError || typeof teamTagsCount !== "number") {
        window.analytics.track("Failed to add a tag in to guild");
        message.error("Error getting a new tag color. Please try again");
        setLoading(false);
        throw new Error(
          "Error in getNewTagColor: could not fetch total tags count"
        );
      }
      if (teamTagsCount < TAG_COLORS.length) {
        tagColor = TAG_COLORS[teamTagsCount];
      } else {
        tagColor = TAG_COLORS[teamTagsCount % TAG_COLORS.length];
      }
      return tagColor;
    };
    /**
     * Checks if tag is an empty string
     *
     * @param {*} tag: string - The tag to check
     * @returns  wether the tag is an empty string or not
     */
    const checkEmptyTag = (tag: string) => {
      const parsedTag = tag.split(" ").filter((item) => item);
      return parsedTag.length > 0;
    };
    const tagColor = await getNewTagColor();
    const parsedTag = checkEmptyTag(tag.name);
    if (parsedTag) {
      const insertTag = {
        name: tag.name,
        color: tagColor,
        workspace,
        description: tag.description ? tag.description : null,
        guild: "",
      };
      insertTag.guild = guildId;

      const { data, error } = await supaClient.from("tags").insert([insertTag]);
      if (data) {
        window.analytics.track("New team tag added");
        setGuildTags((prev) => [...prev, data[0]]);
      }
      if (error) {
        console.error("Error adding tag", error);
        window.analytics.track("Guild add tag error");
        message.error("Error adding tag. Please try again");
      }
      setLoading(false);
    }
  };

  /**
   *  Purpose: Enable force deletion of tags associated to memebers
   */
  const deleteTag = async () => {
    setLoading(true);
    // First we delete the user_tag records
    // This is necessary, otherwise we will get an error when deleting the tag
    const { data: userTagData, error: userTagError } = await supaClient
      .from("discord_user_tags")
      .delete()
      .match({ tag: selectedTag.id });
    if (userTagError) {
      setLoading(false);
      setIsDeleteTagModalVisible(false);
      setIsEditTagModalVisible(false);
      // message.error("Something went wrong. Please try again");
    }
    if (userTagData) {
      // Remove the actual tag
      const { data, error } = await supaClient
        .from("tags")
        .delete()
        .match({ id: selectedTag.id });

      if (error) {
        setLoading(false);
        setIsDeleteTagModalVisible(false);
        setIsEditTagModalVisible(false);
        console.log("error deleting tag", error);
        // message.error("Error deleting tag - Please try again");
      }
      if (data) {
        setIsDeleteTagModalVisible(false);
        setIsEditTagModalVisible(false);
        await loadGuildTags();
        setLoading(false);
        // window.analytics.track("Deleted Tag");
        // message.success("Deleted successfully");
      }
    }
  };

  useEffect(() => {
    loadGuildTags();
  }, [guildId]);

  if (!guildId) {
    return {
      data: [],
      isLoading: loading,
      selectedTag,
      isEditTagModalVisible,
      isDeleteTagModalVisible,
      setSelectedTag,
      setIsEditTagModalVisible,
      setIsDeleteTagModalVisible,
      updateTag,
      deleteTag,
      addTag,
    };
  }

  return {
    data: guildTags,
    isLoading: loading,
    selectedTag,
    isEditTagModalVisible,
    isDeleteTagModalVisible,
    setSelectedTag,
    setIsEditTagModalVisible,
    setIsDeleteTagModalVisible,
    updateTag,
    deleteTag,
    isAddTagModalVisible,
    setIsAddTagModalVisible,
    addTag,
  };
}

export default useGuildTags;
